import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Pagination,
    Select,
    TextField,
    Typography,
} from '@mui/material'
import AddCircleIcon from '@mui/icons-material/AddCircle'
import CloseIcon from '@mui/icons-material/Close'
import DeleteForeverIcon from '@mui/icons-material/DeleteForever'
import EditIcon from '@mui/icons-material/Edit'
import SpeedIcon from '@mui/icons-material/Speed'
import TimerIcon from '@mui/icons-material/Timer'
import Swal from 'sweetalert2'

import {
    deleteParametroTiempo,
    getGruposConfeccion,
    getGruposVariedad,
    getParametrosTiempo,
    getSalidasProduccion,
    patchParametroTiempo,
    postParametroTiempo,
} from '../services/apis'
import { LoadingMaestro } from '../components/LoadingMaestro'
import { normalizarArray, normalizarPaginacion, PARAMETROS_TIEMPO_PAGE_SIZE } from '../utils/apiResponse'

const getApiErrorMessage = (error, fallback) => {
    const message = error.response?.data?.message

    if (Array.isArray(message)) {
        return message.join('\n')
    }

    return message || fallback
}

const parametroInicial = {
    codMaquina: '',
    codGrupoConfeccion: '',
    codGrupoVariedad: '',
    peso: '',
    valor: '',
}

const getParametroKey = (parametro) => (
    `${parametro.codMaquina}-${parametro.codGrupoConfeccion}-${parametro.codGrupoVariedad}-${parametro.peso}`
)

export const ParametrosTiempo = () => {
    const [parametrosTiempo, setParametrosTiempo] = useState([])
    const [salidasProduccion, setSalidasProduccion] = useState([])
    const [gruposConfeccion, setGruposConfeccion] = useState([])
    const [gruposVariedad, setGruposVariedad] = useState([])
    const [filtroMaquina, setFiltroMaquina] = useState('todos')
    const [filtroGrupoConfeccion, setFiltroGrupoConfeccion] = useState('todos')
    const [filtroGrupoVariedad, setFiltroGrupoVariedad] = useState('todos')
    const [filtroPeso, setFiltroPeso] = useState('')
    const [filtroValor, setFiltroValor] = useState('')
    const [pagina, setPagina] = useState(1)
    const [paginacion, setPaginacion] = useState({ page: 1, limit: PARAMETROS_TIEMPO_PAGE_SIZE, total: 0, totalPages: 1 })
    const [loading, setLoading] = useState(true)
    const [openParametro, setOpenParametro] = useState(false)
    const [parametroEditando, setParametroEditando] = useState(null)
    const [parametroEliminar, setParametroEliminar] = useState(null)
    const [parametroForm, setParametroForm] = useState(parametroInicial)

    const cargarDatos = useCallback(async () => {
        setLoading(true)
        try {
            const [parametrosResponse, salidasResponse, gruposConfeccionResponse, gruposVariedadResponse] = await Promise.all([
                getParametrosTiempo({
                    page: pagina,
                    limit: PARAMETROS_TIEMPO_PAGE_SIZE,
                    codMaquina: filtroMaquina !== 'todos' ? filtroMaquina : undefined,
                    codGrupoConfeccion: filtroGrupoConfeccion !== 'todos' ? filtroGrupoConfeccion : undefined,
                    codGrupoVariedad: filtroGrupoVariedad !== 'todos' ? filtroGrupoVariedad : undefined,
                    peso: filtroPeso.trim() || undefined,
                    valor: filtroValor.trim() || undefined,
                }),
                getSalidasProduccion(),
                getGruposConfeccion(),
                getGruposVariedad(),
            ])
            const parametrosData = normalizarArray(parametrosResponse, 'parametrosTiempo')

            setParametrosTiempo(parametrosData)
            setPaginacion(normalizarPaginacion(parametrosResponse, parametrosData.length))
            setSalidasProduccion(normalizarArray(salidasResponse, 'salidasProduccion'))
            setGruposConfeccion(normalizarArray(gruposConfeccionResponse, 'gruposConfeccion'))
            setGruposVariedad(normalizarArray(gruposVariedadResponse, 'gruposVariedad'))
        } catch (error) {
            console.error('Error al cargar parametros tiempo:', error)
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: getApiErrorMessage(error, 'Error al cargar los parametros tiempo'),
                timer: 2500,
                showConfirmButton: false,
            })
        } finally {
            setLoading(false)
        }
    }, [filtroGrupoConfeccion, filtroGrupoVariedad, filtroMaquina, filtroPeso, filtroValor, pagina])

    useEffect(() => {
        cargarDatos()
    }, [cargarDatos])

    useEffect(() => {
        setPagina(1)
    }, [filtroMaquina, filtroGrupoConfeccion, filtroGrupoVariedad, filtroPeso, filtroValor])

    const salidasPorCodigo = useMemo(() => (
        salidasProduccion.reduce((acc, salida) => {
            acc[salida.codMaquina] = salida.desMaquina
            return acc
        }, {})
    ), [salidasProduccion])

    const gruposConfeccionPorCodigo = useMemo(() => (
        gruposConfeccion.reduce((acc, grupo) => {
            acc[grupo.codGrupoConfeccion] = grupo.desGrupoConfeccion
            return acc
        }, {})
    ), [gruposConfeccion])

    const gruposVariedadPorCodigo = useMemo(() => (
        gruposVariedad.reduce((acc, grupo) => {
            acc[grupo.codGrupoVariedad] = grupo.desGrupoVariedad
            return acc
        }, {})
    ), [gruposVariedad])

    const parametrosFiltrados = useMemo(() => (
        [...parametrosTiempo]
            .filter((parametro) => (
                filtroMaquina === 'todos' || Number(parametro.codMaquina) === Number(filtroMaquina)
            ))
            .filter((parametro) => (
                filtroGrupoConfeccion === 'todos' || String(parametro.codGrupoConfeccion) === String(filtroGrupoConfeccion)
            ))
            .filter((parametro) => (
                filtroGrupoVariedad === 'todos' || String(parametro.codGrupoVariedad) === String(filtroGrupoVariedad)
            ))
            .filter((parametro) => (
                !filtroPeso.trim() || String(parametro.peso) === filtroPeso.trim()
            ))
            .filter((parametro) => (
                !filtroValor.trim() || String(parametro.valor) === filtroValor.trim()
            ))
            .sort((parametroA, parametroB) => (
                Number(parametroA.codMaquina ?? 0) - Number(parametroB.codMaquina ?? 0)
                || String(parametroA.codGrupoConfeccion ?? '').localeCompare(String(parametroB.codGrupoConfeccion ?? ''))
                || String(parametroA.codGrupoVariedad ?? '').localeCompare(String(parametroB.codGrupoVariedad ?? ''))
                || Number(parametroA.peso ?? 0) - Number(parametroB.peso ?? 0)
            ))
    ), [filtroGrupoConfeccion, filtroGrupoVariedad, filtroMaquina, filtroPeso, filtroValor, parametrosTiempo])

    const resetParametroForm = () => {
        setParametroForm(parametroInicial)
    }

    const abrirCrearParametro = () => {
        resetParametroForm()
        setParametroEditando(null)
        setOpenParametro(true)
    }

    const abrirEditarParametro = (parametro) => {
        setParametroForm({
            codMaquina: parametro.codMaquina ?? '',
            codGrupoConfeccion: parametro.codGrupoConfeccion ?? '',
            codGrupoVariedad: parametro.codGrupoVariedad ?? '',
            peso: parametro.peso ?? '',
            valor: parametro.valor ?? '',
        })
        setParametroEditando(parametro)
        setOpenParametro(true)
    }

    const cerrarParametro = () => {
        setOpenParametro(false)
        setParametroEditando(null)
        resetParametroForm()
    }

    const handleChangeParametro = (event) => {
        const { name, value } = event.target

        setParametroForm((parametroActual) => ({
            ...parametroActual,
            [name]: value,
        }))
    }

    const guardarParametro = async (event) => {
        event.preventDefault()

        const parametroEnvio = {
            codMaquina: Number(parametroForm.codMaquina),
            codGrupoConfeccion: String(parametroForm.codGrupoConfeccion),
            codGrupoVariedad: String(parametroForm.codGrupoVariedad),
            peso: Number(parametroForm.peso),
            valor: Number(parametroForm.valor),
        }

        try {
            if (parametroEditando) {
                await patchParametroTiempo(
                    parametroEditando.codMaquina,
                    parametroEditando.codGrupoConfeccion,
                    parametroEditando.codGrupoVariedad,
                    parametroEditando.peso,
                    { valor: parametroEnvio.valor }
                )
            } else {
                await postParametroTiempo(parametroEnvio)
            }

            cerrarParametro()
            cargarDatos()
            Swal.fire({
                icon: 'success',
                title: parametroEditando ? 'Parametro actualizado' : 'Parametro creado',
                showConfirmButton: false,
                timer: 1500,
            })
        } catch (error) {
            console.error('Error al guardar el parametro tiempo:', error)
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: getApiErrorMessage(error, 'Error al guardar el parametro tiempo'),
                timer: 2500,
                showConfirmButton: false,
            })
        }
    }

    const confirmarEliminarParametro = async () => {
        if (!parametroEliminar) return

        try {
            await deleteParametroTiempo(
                parametroEliminar.codMaquina,
                parametroEliminar.codGrupoConfeccion,
                parametroEliminar.codGrupoVariedad,
                parametroEliminar.peso
            )
            setParametroEliminar(null)
            cargarDatos()
            Swal.fire({
                icon: 'success',
                title: 'Parametro eliminado',
                showConfirmButton: false,
                timer: 1500,
            })
        } catch (error) {
            console.error('Error al eliminar el parametro tiempo:', error)
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: getApiErrorMessage(error, 'Error al eliminar el parametro tiempo'),
                timer: 2500,
                showConfirmButton: false,
            })
        }
    }

    return (
        <Grid container direction="row" size={{ xs: 12 }} sx={{ justifyContent: "center", alignItems: "center" }}>
            <Grid container direction="row" size={{ xs: 10 }} sx={{ justifyContent: "start", alignItems: "start", mt: 3 }} className='fade-in ajustes-compact' spacing={2}>
                <Grid container direction="row" size={{ xs: 12 }} sx={{ justifyContent: "space-between", alignItems: "center" }} spacing={2}>
                    <Grid container direction="column" size={{ xs: 12, md: 6 }} sx={{ alignItems: "start" }}>
                        <Typography variant='h4'>
                            Parametros tiempo
                        </Typography>

                        <Typography variant='body2' sx={{ color: 'text.secondary' }}>
                            Unidades por minuto por salida de produccion, grupo de confeccion, grupo de variedad y peso.
                        </Typography>
                    </Grid>

                    <Grid container size={{ xs: 12, sm: 5, md: 3 }} className='card-main' sx={{ p: 1.5, alignItems: "center" }}>
                        <TimerIcon color="primary" sx={{ mr: 1 }} />
                        <Box>
                            <Typography variant="caption">Registros totales</Typography>
                            <Typography variant="h5">{paginacion.total}</Typography>
                        </Box>
                    </Grid>

                    <Grid container direction="row" size={{ xs: 12, sm: 5, md: 2 }} sx={{ justifyContent: { xs: "start", sm: "end" } }}>
                        <Button variant="contained" fullWidth onClick={abrirCrearParametro}>
                            <AddCircleIcon />&nbsp;Nuevo parámetro
                        </Button>
                    </Grid>

                    <Divider />
                </Grid>

                <Grid container direction="row" size={{ xs: 12 }} spacing={2} sx={{ alignItems: "center" }}>
                    <Grid container size={{ xs: 12, md: 4 }}>
                        <FormControl fullWidth>
                            <InputLabel>Salida produccion</InputLabel>
                            <Select label="Salida produccion" value={filtroMaquina} onChange={(event) => setFiltroMaquina(event.target.value)}>
                                <MenuItem value="todos">Todas</MenuItem>
                                {salidasProduccion.map((salida) => (
                                    <MenuItem key={salida.codMaquina} value={salida.codMaquina}>
                                        {salida.codMaquina} - {salida.desMaquina}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>

                    <Grid container size={{ xs: 12, md: 4 }}>
                        <FormControl fullWidth>
                            <InputLabel>Grupo confeccion</InputLabel>
                            <Select label="Grupo confeccion" value={filtroGrupoConfeccion} onChange={(event) => setFiltroGrupoConfeccion(event.target.value)}>
                                <MenuItem value="todos">Todos</MenuItem>
                                {gruposConfeccion.map((grupo) => (
                                    <MenuItem key={grupo.codGrupoConfeccion} value={grupo.codGrupoConfeccion}>
                                        {grupo.codGrupoConfeccion} - {grupo.desGrupoConfeccion}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>

                    <Grid container size={{ xs: 12, md: 4 }}>
                        <FormControl fullWidth>
                            <InputLabel>Grupo variedad</InputLabel>
                            <Select label="Grupo variedad" value={filtroGrupoVariedad} onChange={(event) => setFiltroGrupoVariedad(event.target.value)}>
                                <MenuItem value="todos">Todos</MenuItem>
                                {gruposVariedad.map((grupo) => (
                                    <MenuItem key={grupo.codGrupoVariedad} value={grupo.codGrupoVariedad}>
                                        {grupo.codGrupoVariedad} - {grupo.desGrupoVariedad}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>

                    <Grid container size={{ xs: 12, md: 4 }}>
                        <TextField
                            label="Peso"
                            type="number"
                            value={filtroPeso}
                            onChange={(event) => setFiltroPeso(event.target.value)}
                            fullWidth
                            inputProps={{ min: 0, step: 0.01 }}
                        />
                    </Grid>

                    <Grid container size={{ xs: 12, md: 4 }}>
                        <TextField
                            label="Unidades/min"
                            type="number"
                            value={filtroValor}
                            onChange={(event) => setFiltroValor(event.target.value)}
                            fullWidth
                            inputProps={{ min: 0, step: 0.01 }}
                        />
                    </Grid>
                </Grid>

                {loading ? (
                    <LoadingMaestro label="Cargando parametros tiempo..." />
                ) : (
                <>
                <Grid container direction="row" size={{ xs: 12 }} sx={{ justifyContent: "start", alignItems: "stretch", mt: 1 }} spacing={2}>
                    {parametrosFiltrados.map((parametro) => (
                        <Grid key={getParametroKey(parametro)} container direction="column" size={{ xs: 12, md: 6, xl: 4 }} className='card-main' sx={{ p: 2, gap: 1.5, justifyContent: "space-between" }}>
                            <Grid container direction="row" size={{ xs: 12 }} sx={{ justifyContent: "space-between", alignItems: "start", gap: 1 }}>
                                <Box sx={{ minWidth: 0 }}>
                                    <Typography variant="h6" sx={{ lineHeight: 1.15 }}>
                                        {parametro.salidas_produccion?.desMaquina || salidasPorCodigo[parametro.codMaquina] || `Maquina ${parametro.codMaquina}`}
                                    </Typography>

                                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                        Codigo maquina: {parametro.codMaquina}
                                    </Typography>
                                </Box>

                                <Chip size="small" color="primary" label={`${parametro.valor} uds/min`} />
                            </Grid>

                            <Grid container direction="row" size={{ xs: 12 }} sx={{ gap: 1 }}>
                                <Chip
                                    size="small"
                                    label={`${parametro.codGrupoConfeccion} - ${parametro.grupos_confeccion?.desGrupoConfeccion || gruposConfeccionPorCodigo[parametro.codGrupoConfeccion] || 'Grupo confeccion'}`}
                                    variant="outlined"
                                />

                                <Chip
                                    size="small"
                                    label={`${parametro.codGrupoVariedad} - ${parametro.grupos_variedad?.desGrupoVariedad || gruposVariedadPorCodigo[parametro.codGrupoVariedad] || 'Grupo variedad'}`}
                                    variant="outlined"
                                />
                            </Grid>

                            <Grid container direction="row" size={{ xs: 12 }} sx={{ alignItems: "center", gap: 1 }}>
                                <SpeedIcon color="primary" />
                                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                    Peso: {parametro.peso} kg
                                </Typography>
                            </Grid>

                            <Grid container direction="row" size={{ xs: 12 }} sx={{ justifyContent: "end", gap: 1 }}>
                                <Button variant="outlined" size="small" onClick={() => abrirEditarParametro(parametro)}>
                                    <EditIcon />&nbsp;Editar
                                </Button>

                                <Button variant="outlined" color="error" size="small" onClick={() => setParametroEliminar(parametro)}>
                                    <DeleteForeverIcon />&nbsp;Eliminar
                                </Button>
                            </Grid>
                        </Grid>
                    ))}

                    {parametrosFiltrados.length === 0 && (
                        <Grid container direction="column" size={{ xs: 12 }} className='card-main' sx={{ p: 3, alignItems: "center" }}>
                            <TimerIcon color="primary" sx={{ fontSize: 48, mb: 1 }} />
                            <Typography variant="h6">
                                No hay parametros tiempo
                            </Typography>
                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                Ajusta los filtros o crea un nuevo parametro.
                            </Typography>
                        </Grid>
                    )}
                </Grid>

                <Grid container direction="row" size={{ xs: 12 }} sx={{ justifyContent: "space-between", alignItems: "center", mt: 2, gap: 1 }}>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        Mostrando {parametrosFiltrados.length} de {paginacion.total} parametros
                    </Typography>

                    <Pagination
                        count={paginacion.totalPages}
                        page={pagina}
                        onChange={(_, value) => setPagina(value)}
                        color="primary"
                    />
                </Grid>
                </>
                )}
            </Grid>

            <Dialog open={openParametro} onClose={cerrarParametro} fullWidth maxWidth="md">
                <form onSubmit={guardarParametro}>
                    <DialogTitle>
                        <Grid container direction="row" size={{ xs: 12 }} sx={{ justifyContent: "space-between", alignItems: "center" }}>
                            <Typography variant="h5">
                                {parametroEditando ? 'Editar parametro tiempo' : 'Nuevo parámetro tiempo'}
                            </Typography>

                            <Button variant="text" color="negro" onClick={cerrarParametro}>
                                <CloseIcon />
                            </Button>
                        </Grid>
                    </DialogTitle>

                    <DialogContent>
                        <Grid container direction="row" size={{ xs: 12 }} sx={{ justifyContent: "center", alignItems: "center", pt: 1 }} spacing={2}>
                            <Grid container size={{ xs: 12, md: 6 }}>
                                <FormControl fullWidth disabled={Boolean(parametroEditando)}>
                                    <InputLabel>Salida produccion</InputLabel>
                                    <Select
                                        label="Salida produccion"
                                        name="codMaquina"
                                        value={parametroForm.codMaquina}
                                        onChange={handleChangeParametro}
                                        required
                                    >
                                        {salidasProduccion.map((salida) => (
                                            <MenuItem key={salida.codMaquina} value={salida.codMaquina}>
                                                {salida.codMaquina} - {salida.desMaquina}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>

                            <Grid container size={{ xs: 12, md: 6 }}>
                                <FormControl fullWidth disabled={Boolean(parametroEditando)}>
                                    <InputLabel>Grupo confeccion</InputLabel>
                                    <Select
                                        label="Grupo confeccion"
                                        name="codGrupoConfeccion"
                                        value={parametroForm.codGrupoConfeccion}
                                        onChange={handleChangeParametro}
                                        required
                                    >
                                        {gruposConfeccion.map((grupo) => (
                                            <MenuItem key={grupo.codGrupoConfeccion} value={grupo.codGrupoConfeccion}>
                                                {grupo.codGrupoConfeccion} - {grupo.desGrupoConfeccion}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>

                            <Grid container size={{ xs: 12, md: 6 }}>
                                <FormControl fullWidth disabled={Boolean(parametroEditando)}>
                                    <InputLabel>Grupo variedad</InputLabel>
                                    <Select
                                        label="Grupo variedad"
                                        name="codGrupoVariedad"
                                        value={parametroForm.codGrupoVariedad}
                                        onChange={handleChangeParametro}
                                        required
                                    >
                                        {gruposVariedad.map((grupo) => (
                                            <MenuItem key={grupo.codGrupoVariedad} value={grupo.codGrupoVariedad}>
                                                {grupo.codGrupoVariedad} - {grupo.desGrupoVariedad}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>

                            <Grid container size={{ xs: 12, md: 3 }}>
                                <TextField
                                    label="Peso"
                                    name="peso"
                                    type="number"
                                    value={parametroForm.peso}
                                    onChange={handleChangeParametro}
                                    fullWidth
                                    required
                                    disabled={Boolean(parametroEditando)}
                                    inputProps={{ min: 0, step: 0.01 }}
                                />
                            </Grid>

                            <Grid container size={{ xs: 12, md: 3 }}>
                                <TextField
                                    label="Unidades por minuto"
                                    name="valor"
                                    type="number"
                                    value={parametroForm.valor}
                                    onChange={handleChangeParametro}
                                    fullWidth
                                    required
                                    inputProps={{ min: 0, step: 0.01 }}
                                />
                            </Grid>
                        </Grid>
                    </DialogContent>

                    <DialogActions>
                        <Button color="primary" onClick={cerrarParametro}>
                            Cancelar
                        </Button>

                        <Button color="primary" variant="contained" type="submit">
                            {parametroEditando ? 'Guardar parametro' : 'Crear parametro'}
                        </Button>
                    </DialogActions>
                </form>
            </Dialog>

            <Dialog open={Boolean(parametroEliminar)} onClose={() => setParametroEliminar(null)} fullWidth maxWidth="xs">
                <DialogTitle>
                    Eliminar parametro tiempo
                </DialogTitle>

                <DialogContent>
                    <Typography>
                        Desea eliminar el parametro de {salidasPorCodigo[parametroEliminar?.codMaquina] || parametroEliminar?.codMaquina || ''}?
                    </Typography>
                </DialogContent>

                <DialogActions>
                    <Button color="primary" onClick={() => setParametroEliminar(null)}>
                        Cancelar
                    </Button>

                    <Button color="error" variant="contained" onClick={confirmarEliminarParametro}>
                        Eliminar
                    </Button>
                </DialogActions>
            </Dialog>
        </Grid>
    )
}
